import { useState, useEffect, useRef } from 'react';
import { cloneDeep } from 'lodash';
import { v4 as uuidv4 } from 'uuid';

import { Box, Button, Tab, Tabs, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import ComputerOutlinedIcon from '@mui/icons-material/ComputerOutlined';
import TabletMacOutlinedIcon from '@mui/icons-material/TabletMacOutlined';
import PhoneIphoneOutlinedIcon from '@mui/icons-material/PhoneIphoneOutlined';

// Hooks
import useDataSourceManager from 'hooks/useDataSourceManager';
import useAuthScope from 'hooks/useAuthScope';

// Components
import ModalContainer from 'components/ModalContainer';
import GlobalAlert from 'components/Notification';
import { SolidButton } from 'components/buttons/SolidButton';
import { SolidButton as SolidRectangleButton } from 'components/buttons/SolidRectangleButton';
import { OutlineButton } from 'components/buttons/OutlineButton';

import Loading from '../components/Loading';
import LoginPreview from './components/PreviewTab/LoginPreview';
import LandingPreview from './components/PreviewTab/LandingPreview';
import MessagesSidebar from './components/MessagesSidebar';
import MessagesPreview from './components/PreviewTab/MessagesPreview';
import TermsPreview from './components/PreviewTab/TermsPreview';

import MultipleLanguagesModal from './Modal/MultipleLanguagesModal';

// Const
import { EDITOR_TABS, ALL_LANGUAGE_OPTIONS, DEFAULT_LANGUAGE_CONFIG, DEFAULT_IMAGE, DEFAULT_LOGIN, DEFAULT_LANDING, DEFAULT_MESSAGES, DEFAULT_TERMS } from './const';

const DEVICE_PREVIEW_OPTIONS = [
  { value: 'desktop', icon: ComputerOutlinedIcon },
  { value: 'tablet', icon: TabletMacOutlinedIcon },
  { value: 'mobile', icon: PhoneIphoneOutlinedIcon },
];

const checkMockup = process.env.REACT_APP_IS_MOCKUP && process.env.REACT_APP_IS_MOCKUP === 'true' ? true : false;

const SAVE_TIMEOUT_MS = 5 * 60 * 1000;

const TAB_CONTENT_SX = {
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '12px',
};

// login/landing/terms are keyed by element id; messages is a flat list matched by id.
const DOMAIN_KEYS = ['login', 'landing', 'terms'];

const findDomainEntry = (id, domains) => {
  for (const domainKey of DOMAIN_KEYS) {
    if (domains[domainKey]?.[id]) {
      return { domainKey, entry: domains[domainKey][id] };
    }
  }

  const message = domains.messages?.find(msg => msg.id === id);
  if (message) {
    return { domainKey: 'messages', entry: message };
  }

  return null;
};

// Flatten the currently-edited text out of login/landing/terms/messages into a
// { elementId: text } map, in the same shape as one i18n.locales[] record.
const collectLocaleTexts = ({ login, landing, terms, messages }) => {
  const texts = {};

  for (const domain of [login, landing, terms]) {
    for (const [id, entry] of Object.entries(domain || {})) {
      if (entry?.text !== undefined) {
        texts[id] = entry.text;
      }
    }
  }

  for (const msg of messages || []) {
    if (msg?.text !== undefined) {
      texts[msg.id] = msg.text;
    }
  }

  return texts;
};

// Write a locale record's text onto shallow copies of login/landing/terms/messages,
// leaving style/show untouched since those aren't locale-specific.
const applyLocaleTexts = (usedLangTexts, { login, landing, terms, messages }) => {
  const nextLogin = { ...login };
  const nextLanding = { ...landing };
  const nextTerms = { ...terms };
  const nextMessages = (messages || []).map(msg => ({ ...msg }));

  const ids = [...Object.keys(nextLogin), ...Object.keys(nextLanding), ...Object.keys(nextTerms), ...nextMessages.map(msg => msg.id)];

  for (const id of ids) {
    if (usedLangTexts?.[id] === undefined) continue;
    const domainMatch = findDomainEntry(id, { login: nextLogin, landing: nextLanding, terms: nextTerms, messages: nextMessages });
    if (domainMatch) {
      domainMatch.entry.text = usedLangTexts[id];
    }
  }

  return { login: nextLogin, landing: nextLanding, terms: nextTerms, messages: nextMessages };
};

const parseFontSize = v => {
  const n = parseFloat(String(v).replace(/[^0-9.-]+/g, ''));
  return Number.isFinite(n) ? n : undefined;
};

const checkDisplayOn = (item, selectedProfileItem) => {
  const usedAuthTypeList = item?.authType;

  if (usedAuthTypeList?.[0] === 'ALL') {
    return true;
  } else if (usedAuthTypeList?.some(authType => selectedProfileItem?.config?.authType?.includes(authType))) {
    return true;
  } else {
    return false;
  }
};

const SplashPageEditor = ({
  openEditorModal,
  setOpenEditorModal,
  selectedProfileItem = null,
  tableData = [],
  defaultSplashPageConfig = {},
  isDefaultConfigLoading = false,
  initCaptivePortal = () => {},
  showEditGeneral,
}) => {
  const theme = useTheme();
  const accentColor = theme.palette.primary.mainHover;

  const { auth } = useAuthScope();
  const { dataMap, getData, sendRequest } = useDataSourceManager();
  const [saveSplashPageUuid] = useState(() => uuidv4());
  const saveTimeoutRef = useRef(null);

  const [isSaving, setIsSaving] = useState(false);
  const [alertInfo, setAlertInfo] = useState({ open: false, severity: 'success', message: '' });
  const [isLoading, setIsLoading] = useState(false);

  const [newSplashPageConfig, setNewSplashPageConfig] = useState(null);

  const [isEditMode, setIsEditMode] = useState(true);
  const [devicePreview, setDevicePreview] = useState('desktop');
  const [activeTab, setActiveTab] = useState('login');
  const [selectedMessageId, setSelectedMessageId] = useState('text.login.fail.occupied');

  const [openMultipleLanguagesModal, setOpenMultipleLanguagesModal] = useState(false);
  const [languageConfig, setLanguageConfig] = useState(DEFAULT_LANGUAGE_CONFIG);
  const [previewLanguageOptions, setPreviewLanguageOptions] = useState(languageConfig.supportLanguages);
  const [previewSelectedLanguage, setPreviewSelectedLanguage] = useState(languageConfig.defaultLanguage);

  // previewSelectedLanguage (state) is for display only — reading it back to decide "which
  // locale does the on-screen text belong to" is racy, since a state update isn't visible to a
  // closure until the next render. activeLocaleIdRef is updated synchronously the instant we
  // decide to switch, so anything that needs the *current* active locale (committing text on
  // switch, committing text on Save) must read the ref, never the state.
  const activeLocaleIdRef = useRef(languageConfig.defaultLanguage);
  const setActiveLocale = nextLocaleId => {
    activeLocaleIdRef.current = nextLocaleId;
    setPreviewSelectedLanguage(nextLocaleId);
  };

  const [imageInfo, setImageInfo] = useState(DEFAULT_IMAGE);
  const [login, setLogin] = useState(DEFAULT_LOGIN);
  const [landing, setLanding] = useState(DEFAULT_LANDING);
  const [messages, setMessages] = useState(DEFAULT_MESSAGES);
  const [terms, setTerms] = useState(DEFAULT_TERMS);

  const selectedTableItem = tableData?.find(item => item.id === selectedProfileItem?.id);
  const captivePortalName = selectedTableItem?.captivePortalName || '';

  const resetEditorState = () => {
    setIsEditMode(openEditorModal?.mode === 'edit');
    setDevicePreview('desktop');
    setActiveTab('login');
    setSelectedMessageId('text.login.fail.occupied');
    setMessages(prev => prev.map(item => ({ ...item, visible: item.id === 'text.login.fail.occupied' })));
  };

  const handleCloseBtn = () => {
    clearTimeout(saveTimeoutRef.current);
    setIsSaving(false);
    resetEditorState();
    setOpenEditorModal({ mode: 'edit', open: false });
  };

  const handleClickSaveBtn = () => {
    const finalConfig = cloneDeep(newSplashPageConfig);

    for (const [blockKey, blockValue] of Object.entries(finalConfig.data || {})) {
      const imageInfoKey = blockValue?.id;
      const trackedImage = imageInfo[imageInfoKey];

      if (trackedImage) {
        const newImageContent = trackedImage?.style?.content?.replace(/^.*base64,/, '');
        finalConfig.data[blockKey].content = newImageContent || '';
        finalConfig.data[blockKey].type = trackedImage?.style?.type || finalConfig.data[blockKey].type;
      }
    }

    // Commit whatever's currently on screen into its own locale record before building the final payload.
    const locales = finalConfig?.i18n?.locales || [];
    const activeLocaleIndex = locales.findIndex(locale => locale.id === activeLocaleIdRef.current);
    if (activeLocaleIndex !== -1) {
      const collected = collectLocaleTexts({ login, landing, terms, messages });
      finalConfig.i18n.locales[activeLocaleIndex] = { ...finalConfig.i18n.locales[activeLocaleIndex], ...collected };
    }

    for (const [elementKey, elementValue] of Object.entries(finalConfig.elements || {})) {
      const domainMatch = findDomainEntry(elementValue?.id, { login, landing, terms, messages });
      const entryStyle = domainMatch?.entry?.style;

      // Background color: either a plain background (imageInfo) or a button's background color.
      if (elementValue?.style?.['background-color']?.hex) {
        const backgroundColor = imageInfo[elementValue?.id]?.style?.backgroundColor || entryStyle?.buttonColor;
        if (backgroundColor) {
          finalConfig.elements[elementKey].style['background-color'].hex = backgroundColor;
        }
      }

      // Font color
      if (elementValue?.style?.['color']?.hex && entryStyle?.fontColor) {
        finalConfig.elements[elementKey].style['color'].hex = entryStyle.fontColor;
      }

      // Font size
      if (elementValue?.style?.['font-size'] && entryStyle?.fontSize) {
        finalConfig.elements[elementKey].style['font-size'] = `${entryStyle.fontSize}px`;
      }

      // Font family
      if (elementValue?.style?.['font-family'] && entryStyle?.fontFamily) {
        finalConfig.elements[elementKey].style['font-family'] = entryStyle.fontFamily;
      }
    }

    const standalone = !auth.orgs || Object.keys(auth.orgs).length === 0;
    const baseUrl = standalone ? `/orgs/:orgId` : `/ents/:entId/orgs/:orgId`;

    const apiConfig = {
      org: `${baseUrl}/settings/captivePortals/:id/splashpage.update`,
      site: `${baseUrl}/sites/:siteId/settings/captivePortals/:id/splashpage.update`,
    };

    const apiKey = apiConfig[auth.scopeLevel];

    const restfulParams = standalone
      ? { orgId: auth.realm.id, siteId: auth.sites?.id, id: selectedProfileItem?.id || '' }
      : {
          entId: auth.realm.id,
          orgId: auth.orgs.id,
          siteId: auth.sites?.id,
          id: selectedProfileItem?.id || '',
        };

    const payload = {
      splashPageConfig: finalConfig,
    };

    setIsSaving(true);
    sendRequest(apiKey, saveSplashPageUuid, restfulParams, payload);

    clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      setIsSaving(false);
      setAlertInfo({ open: true, severity: 'error', message: 'Save request timed out. Please try again.' });
    }, SAVE_TIMEOUT_MS);
  };

  // Switches which locale's text is loaded into the editable state (login/landing/terms/messages).
  // Commits whatever's currently on screen back into the outgoing locale's own record first, so
  // edits made in one language are never lost or bled into another language's record.
  const switchPreviewLanguage = nextLocaleId => {
    if (!nextLocaleId || nextLocaleId === activeLocaleIdRef.current) return;

    setNewSplashPageConfig(prevConfig => {
      const locales = prevConfig?.i18n?.locales;
      if (!locales) return prevConfig;

      const nextConfig = cloneDeep(prevConfig);
      const prevIndex = locales.findIndex(locale => locale.id === activeLocaleIdRef.current);
      if (prevIndex !== -1) {
        const collected = collectLocaleTexts({ login, landing, terms, messages });
        nextConfig.i18n.locales[prevIndex] = { ...nextConfig.i18n.locales[prevIndex], ...collected };
      }

      const targetLocale = nextConfig.i18n.locales.find(locale => locale.id === nextLocaleId);
      if (targetLocale) {
        const applied = applyLocaleTexts(targetLocale, { login, landing, terms, messages });
        setLogin(applied.login);
        setLanding(applied.landing);
        setTerms(applied.terms);
        setMessages(applied.messages);
      }

      return nextConfig;
    });

    setActiveLocale(nextLocaleId);
  };

  const handleClickMultipleLangSaveBtn = updatedLanguageConfig => {
    const tmpSplashPageConfig = cloneDeep(newSplashPageConfig) || cloneDeep(defaultSplashPageConfig);
    const locales = tmpSplashPageConfig.i18n.locales || (tmpSplashPageConfig.i18n.locales = []);
    const existingIds = new Set(locales.map(l => l.id));

    // Commit whatever's currently on screen back into its own locale record first, so edits
    // made before opening this modal aren't lost when we add/remove languages below.
    const currentLocaleIndex = locales.findIndex(locale => locale.id === activeLocaleIdRef.current);
    if (currentLocaleIndex !== -1) {
      const collected = collectLocaleTexts({ login, landing, terms, messages });
      locales[currentLocaleIndex] = { ...locales[currentLocaleIndex], ...collected };
    }

    // New locales start from the factory-default config's text, not from whatever locale is
    // currently being edited — otherwise a newly added language would silently inherit
    // another language's edits instead of the real default copy.
    const defaultLocales = defaultSplashPageConfig?.i18n?.locales || [];
    const defaultTemplate = defaultLocales.find(locale => locale.id === defaultSplashPageConfig?.i18n?.defaultLocale?.id) || defaultLocales[0] || {};
    const template = { ...defaultTemplate };
    delete template.id;
    delete template.localeName;

    const toAdd = updatedLanguageConfig.supportLanguages.filter(lang => !existingIds.has(lang.value));

    toAdd.forEach(lang => {
      locales.push({
        id: lang.value,
        localeName: lang.label,
        ...cloneDeep(template),
      });
    });

    // Drop any locale record whose language was removed from Support Languages — otherwise
    // i18n.locales just keeps growing forever and a removed language silently comes back
    // the next time the editor is opened.
    const supportedIds = new Set(updatedLanguageConfig.supportLanguages.map(lang => lang.value));
    tmpSplashPageConfig.i18n.locales = tmpSplashPageConfig.i18n.locales.filter(locale => supportedIds.has(locale.id));

    // Update default language
    tmpSplashPageConfig.i18n.defaultLocale = {
      id: updatedLanguageConfig.defaultLanguage,
      localeName: ALL_LANGUAGE_OPTIONS.find(lang => lang.value === updatedLanguageConfig.defaultLanguage)?.label || 'English',
    };

    setNewSplashPageConfig(tmpSplashPageConfig);
    setLanguageConfig(updatedLanguageConfig);
    setOpenMultipleLanguagesModal(false);

    // Reflect the (possibly new) default language's own text in the editable fields right away.
    const targetLocale = tmpSplashPageConfig.i18n.locales.find(locale => locale.id === updatedLanguageConfig.defaultLanguage);
    if (targetLocale) {
      const applied = applyLocaleTexts(targetLocale, { login, landing, terms, messages });
      setLogin(applied.login);
      setLanding(applied.landing);
      setTerms(applied.terms);
      setMessages(applied.messages);
    }
    setActiveLocale(updatedLanguageConfig.defaultLanguage);
  };

  const handleTabChange = (_event, newValue) => {
    if (newValue) {
      setActiveTab(newValue);
    }
  };

  const handleDevicePreviewChange = (_event, newValue) => {
    if (newValue) {
      setDevicePreview(newValue);
    }
  };

  const handleUpdateImageBlock = (blockKey, updates) => {
    setImageInfo(prev => ({
      ...prev,
      [blockKey]: {
        ...prev[blockKey],
        ...updates,
      },
    }));
  };

  const handleUpdateLoginBlock = (blockKey, updates) => {
    setLogin(prev => ({
      ...prev,
      [blockKey]: {
        ...prev[blockKey],
        ...updates,
      },
    }));
  };

  const handleUpdateLandingBlock = (blockKey, updates) => {
    setLanding(prev => ({
      ...prev,
      [blockKey]: {
        ...prev[blockKey],
        ...updates,
      },
    }));
  };

  const handleUpdateMessagesBlock = (messageId, updates) => {
    setMessages(prev => prev.map(item => (item.id === messageId ? { ...item, ...updates } : item)));
  };

  const handleSelectMessage = messageId => {
    setSelectedMessageId(messageId);
    setMessages(prev => prev.map(item => (item.id === messageId ? { ...item, visible: true } : { ...item, visible: false })));
  };

  const handleUpdateMessageStyle = (messageId, style) => {
    setMessages(prev => prev.map(item => (item.id === messageId ? { ...item, style } : item)));
  };

  const handleUpdateTermsBlock = (blockKey, updates) => {
    setTerms(prev => ({
      ...prev,
      [blockKey]: {
        ...prev[blockKey],
        ...updates,
      },
    }));
  };

  const parseSplashPageConfig = splashPageConfig => {
    if (!splashPageConfig) return;

    const { data: imageConfig, elements: elementsConfig, i18n: i18nConfig } = splashPageConfig;
    const targetLogin = { ...login };
    const targetLanding = { ...landing };
    const targetMessages = [...messages];
    const targetTerms = { ...terms };
    const targetImageInfo = { ...imageInfo };
    const targetLanguageConfig = { ...languageConfig };
    let supportLanguageList = [];

    // Parse image config
    if (imageConfig) {
      for (const [_key, value] of Object.entries(imageConfig)) {
        if (targetImageInfo[value?.id]) {
          const imageContent = value?.content ? `data:${value?.type};base64,${value?.content}` : '';
          targetImageInfo[value.id].style.content = imageContent;
          targetImageInfo[value.id].style.type = value?.type || targetImageInfo[value.id].style.type;
          targetImageInfo[value.id].show = checkDisplayOn(targetImageInfo[value.id].displayOn, selectedProfileItem);
        }
      }
    }

    // Parse text, displayOn and style config
    if (elementsConfig) {
      let usedLangTexts = {};
      if (i18nConfig?.locales?.length > 0) {
        const findLocalesIndex = i18nConfig.locales.findIndex(locale => locale.id === i18nConfig.defaultLocale?.id);
        if (findLocalesIndex !== -1) {
          usedLangTexts = i18nConfig.locales[findLocalesIndex];
        } else {
          usedLangTexts = i18nConfig.locales[0];
        }
        supportLanguageList = i18nConfig?.locales?.map(locale => ({ label: locale.localeName, value: locale.id }));
      }

      for (const [_key, value] of Object.entries(elementsConfig)) {
        const fs = parseFontSize(value?.style?.['font-size']);

        if (targetImageInfo[value?.id]) {
          targetImageInfo[value.id].show = checkDisplayOn(targetImageInfo[value.id].displayOn, selectedProfileItem);

          if (targetImageInfo[value.id]?.style?.backgroundColor) {
            targetImageInfo[value.id].style.backgroundColor = value?.style?.['background-color']?.hex || targetImageInfo[value.id].style.backgroundColor;
          }
        }

        const domainMatch = findDomainEntry(value?.id, { login: targetLogin, landing: targetLanding, terms: targetTerms, messages: targetMessages });
        if (domainMatch) {
          const { entry } = domainMatch;

          entry.show = checkDisplayOn(entry.displayOn, selectedProfileItem);

          if (entry.text) {
            entry.text = usedLangTexts?.[value?.id] || entry.text;
          }

          if (entry.style?.fontColor) {
            entry.style.fontColor = value?.style?.['color']?.hex || entry.style.fontColor;
          }

          if (entry.style?.fontSize && fs !== undefined) {
            entry.style.fontSize = fs;
          }

          if (entry.style?.fontFamily) {
            entry.style.fontFamily = value?.style?.['font-family'] || entry.style.fontFamily;
          }

          if (entry.style?.buttonColor) {
            entry.style.buttonColor = value?.style?.['background-color']?.hex || entry.style.buttonColor;
          }
        }
      }
    }

    // Parse language config
    if (i18nConfig) {
      for (const [key, value] of Object.entries(i18nConfig)) {
        if (key === 'defaultLocale') {
          targetLanguageConfig.defaultLanguage = value?.id || targetLanguageConfig.defaultLanguage;
          targetLanguageConfig.supportLanguages = supportLanguageList || targetLanguageConfig.supportLanguages;
        }
      }
    }

    setImageInfo(targetImageInfo);
    setLogin(targetLogin);
    setLanding(targetLanding);
    setMessages(targetMessages);
    setTerms(targetTerms);
    setLanguageConfig(targetLanguageConfig);
  };

  useEffect(() => {
    return () => clearTimeout(saveTimeoutRef.current);
  }, []);

  useEffect(() => {
    if (!Object.hasOwn(dataMap ?? {}, saveSplashPageUuid)) return;

    const saveResponse = getData(saveSplashPageUuid);
    if (!saveResponse) return;

    clearTimeout(saveTimeoutRef.current);
    setIsSaving(false);

    if (checkMockup) {
      if (saveResponse?.data) {
        initCaptivePortal();
        handleCloseBtn();
      }
    } else {
      if (saveResponse.hasOwnProperty('code') && saveResponse?.code !== 200) {
        setAlertInfo({ open: true, severity: 'error', message: saveResponse?.message });
      } else {
        initCaptivePortal();
        handleCloseBtn();
      }
    }
  }, [dataMap?.[saveSplashPageUuid]]);

  useEffect(() => {
    setPreviewLanguageOptions(languageConfig.supportLanguages);
  }, [languageConfig]);

  useEffect(() => {
    // when defaultLanguage in languageConfig changes (e.g. saved from modal), reflect it in preview selection
    if (languageConfig && languageConfig.defaultLanguage) {
      setActiveLocale(languageConfig.defaultLanguage);
    }
  }, [languageConfig.defaultLanguage]);

  useEffect(() => {
    // ensure the active locale is valid for current options (e.g. it was just removed from
    // Support Languages) — fall back to the default language, or the first one available
    const values = previewLanguageOptions.map(o => o.value);
    if (values.includes(activeLocaleIdRef.current)) return;

    const fallbackLanguage = languageConfig?.defaultLanguage && values.includes(languageConfig.defaultLanguage) ? languageConfig.defaultLanguage : values[0] || null;

    if (fallbackLanguage) {
      const targetLocale = newSplashPageConfig?.i18n?.locales?.find(locale => locale.id === fallbackLanguage);
      if (targetLocale) {
        const applied = applyLocaleTexts(targetLocale, { login, landing, terms, messages });
        setLogin(applied.login);
        setLanding(applied.landing);
        setTerms(applied.terms);
        setMessages(applied.messages);
      }
    }

    setActiveLocale(fallbackLanguage);
  }, [previewLanguageOptions]);

  useEffect(() => {
    if (!openEditorModal?.open) return;

    if (!selectedTableItem?.config?.splashPageConfig && isDefaultConfigLoading) {
      setIsLoading(true);
      return;
    }

    resetEditorState(); // Reset to default state when modal is opened

    // If a splash page has never been created and the returned value is null, use the default file (/system/defaultSplashPageConfig) as the editor data.
    const usedSplashPageConfig = selectedTableItem?.config?.splashPageConfig || cloneDeep(defaultSplashPageConfig);
    parseSplashPageConfig(usedSplashPageConfig);
    setNewSplashPageConfig(cloneDeep(usedSplashPageConfig));

    // Reset the preview language selector to this config's own default locale.
    setActiveLocale(usedSplashPageConfig?.i18n?.defaultLocale?.id || DEFAULT_LANGUAGE_CONFIG.defaultLanguage);
    setIsLoading(false);
  }, [openEditorModal, isDefaultConfigLoading]);

  const renderTabContent = () => {
    if (activeTab === 'login') {
      return (
        <Box sx={TAB_CONTENT_SX}>
          <LoginPreview
            devicePreview={devicePreview}
            editable={isEditMode}
            selectedLanguage={previewSelectedLanguage}
            languageOptions={previewLanguageOptions}
            setLanguageOptions={switchPreviewLanguage}
            login={login}
            onUpdateLoginBlock={handleUpdateLoginBlock}
            imageInfo={imageInfo}
            onUpdateImageBlock={handleUpdateImageBlock}
          />
        </Box>
      );
    }

    if (activeTab === 'landing') {
      return (
        <Box sx={TAB_CONTENT_SX}>
          <LandingPreview
            devicePreview={devicePreview}
            editable={isEditMode}
            selectedLanguage={previewSelectedLanguage}
            languageOptions={previewLanguageOptions}
            setLanguageOptions={switchPreviewLanguage}
            landing={landing}
            onUpdateLandingBlock={handleUpdateLandingBlock}
            imageInfo={imageInfo}
            onUpdateImageBlock={handleUpdateImageBlock}
          />
        </Box>
      );
    }

    if (activeTab === 'messages') {
      return (
        <Box sx={{ display: 'flex', width: '100%', height: '100%' }}>
          <MessagesSidebar messages={messages} selectedMessageId={selectedMessageId} onSelectMessage={handleSelectMessage} theme={theme} />
          <Box sx={TAB_CONTENT_SX}>
            <MessagesPreview
              devicePreview={devicePreview}
              editable={isEditMode}
              selectedLanguage={previewSelectedLanguage}
              selectedMessageId={selectedMessageId}
              onSelectMessage={handleSelectMessage}
              onUpdateMessageStyle={handleUpdateMessageStyle}
              messages={messages}
              onUpdateMessagesBlock={handleUpdateMessagesBlock}
              languageOptions={previewLanguageOptions}
              setLanguageOptions={switchPreviewLanguage}
              login={login}
              onUpdateLoginBlock={handleUpdateLoginBlock}
              imageInfo={imageInfo}
              onUpdateImageBlock={handleUpdateImageBlock}
            />
          </Box>
        </Box>
      );
    }

    // Terms
    return (
      <Box sx={TAB_CONTENT_SX}>
        <TermsPreview
          devicePreview={devicePreview}
          editable={isEditMode}
          selectedLanguage={previewSelectedLanguage}
          languageOptions={previewLanguageOptions}
          setLanguageOptions={switchPreviewLanguage}
          terms={terms}
          onUpdateTermsBlock={handleUpdateTermsBlock}
          imageInfo={imageInfo}
          onUpdateImageBlock={handleUpdateImageBlock}
        />
      </Box>
    );
  };

  return (
    <ModalContainer display={openEditorModal?.open} modalWidth="1250px">
      {isLoading && <Loading />}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          margin: '0 0 20px 20px',
        }}
      >
        <Typography
          sx={{
            color: theme.palette.primary.main,
            fontFamily: theme.typography.fontFamily,
            fontSize: theme.typography.customFontSize[5],
            fontWeight: theme.typography.customFontWeight[3],
          }}
        >
          {openEditorModal?.mode === 'preview' ? 'Preview' : 'Edit Captive Portal'}
        </Typography>
        <Button
          onClick={handleCloseBtn}
          sx={{
            backgroundColor: theme.palette.primary.transparent,
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: theme.palette.primary.transparent,
            },
          }}
        >
          <CloseIcon />
        </Button>
      </Box>

      <Box sx={{ margin: '0 20px' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '12px',
            marginBottom: '16px',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <Typography
              sx={{
                color: theme.palette.primary.greyPrimary,
                fontSize: theme.typography.customFontSize[2],
                fontWeight: theme.typography.customFontWeight[3],
              }}
            >
              Captive Portal Name
            </Typography>
            <Typography sx={{ fontSize: theme.typography.customFontSize[2] }}>{captivePortalName}</Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {/* Device Mode - Desktop/Tablet/Mobile */}
            <ToggleButtonGroup
              exclusive
              value={devicePreview}
              onChange={handleDevicePreviewChange}
              sx={{
                '& .MuiToggleButtonGroup-grouped': {
                  margin: 0,
                },
              }}
            >
              {DEVICE_PREVIEW_OPTIONS.map(({ value, icon: Icon }) => (
                <ToggleButton
                  key={value}
                  value={value}
                  sx={{
                    width: 36,
                    height: 30,
                    padding: 0,
                    color: theme.palette.primary.greySecound,
                    backgroundColor: theme.palette.primary.white,
                    '&.Mui-selected': {
                      backgroundColor: accentColor,
                      color: theme.palette.primary.white,
                      '&:hover': {
                        backgroundColor: accentColor,
                      },
                    },
                    '&:hover': {
                      backgroundColor: theme.palette.primary.lightBlue,
                    },
                  }}
                >
                  <Icon sx={{ fontSize: 20 }} />
                </ToggleButton>
              ))}
            </ToggleButtonGroup>

            {/* Button - Quick Preview/Continue Edit */}
            {openEditorModal?.mode === 'edit' && (
              <SolidRectangleButton width="130px" height="30px" onClick={() => setIsEditMode(prev => !prev)}>
                {isEditMode ? 'Quick Preview' : 'Continue Edit'}
              </SolidRectangleButton>
            )}
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            borderBottom: `1px solid ${theme.palette.primary.borderGreyPrimary}`,
          }}
        >
          {/* Tab - Login/Messages/Terms */}
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            sx={{
              flex: 1,
              minHeight: 'auto',
              '& .MuiTabs-indicator': {
                backgroundColor: accentColor,
                borderRadius: 0,
              },
              '.MuiTabs-flexContainer': {
                gap: '8px',
              },
            }}
          >
            {EDITOR_TABS.map(tab => (
              <Tab
                key={tab.value}
                value={tab.value}
                label={tab.label}
                sx={{
                  minHeight: '40px',
                  minWidth: 'auto',
                  padding: '8px 20px',
                  fontSize: theme.typography.customFontSize[2],
                  fontWeight: activeTab === tab.value ? theme.typography.customFontWeight[1] : theme.typography.customFontWeight[0],
                  color: activeTab === tab.value ? accentColor : theme.palette.primary.greySecound,
                }}
              />
            ))}
          </Tabs>

          {/* Multiple Languages button */}
          {openEditorModal?.mode === 'edit' && (
            <Button
              onClick={() => setOpenMultipleLanguagesModal(true)}
              sx={{
                fontSize: theme.typography.customFontSize[2],
                fontWeight: theme.typography.customFontWeight[0],
                color: theme.palette.primary.greySecound,
                backgroundColor: theme.palette.primary.transparent,
                whiteSpace: 'nowrap',
              }}
            >
              Multiple Languages
            </Button>
          )}
        </Box>

        <Box
          sx={{
            marginTop: '16px',
            backgroundColor: theme.palette.primary.background,
            borderRadius: theme.shape.customBorderRadius[1],
            border: `1px solid ${theme.palette.primary.borderGreyPrimary}`,
            maxHeight: '650px',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'auto',
          }}
        >
          {renderTabContent()}
        </Box>

        {/* Footer */}
        {openEditorModal?.mode === 'edit' && (
          <Box
            sx={{
              paddingTop: '20px',
              display: 'flex',
              justifyContent: 'center',
              gap: '20px',
            }}
          >
            <OutlineButton width="100px" height="32px" onClick={handleCloseBtn} disabled={isSaving}>
              Cancel
            </OutlineButton>
            <SolidButton width="100px" height="32px" onClick={handleClickSaveBtn} disabled={!showEditGeneral || isSaving}>
              Save
            </SolidButton>
          </Box>
        )}
      </Box>

      <GlobalAlert open={alertInfo.open} severity={alertInfo.severity} message={alertInfo.message} onClose={() => setAlertInfo(prev => ({ ...prev, open: false }))} />

      {/* Multiple languages modal */}
      <MultipleLanguagesModal
        languageConfig={languageConfig}
        open={openMultipleLanguagesModal}
        onClose={() => setOpenMultipleLanguagesModal(false)}
        onSave={handleClickMultipleLangSaveBtn}
      />
    </ModalContainer>
  );
};

export default SplashPageEditor;
